import { motion } from 'framer-motion';

const fadeUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
};

const PrivacyPage = () => {
    return (
        <div data-testid="privacy-page" className="min-h-screen">
            {/* Hero Section */}
            <section className="relative pt-44 pb-24 bg-slate-900 overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-900 to-blue-900/30"></div>
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 h-full flex items-center justify-center">
                    <motion.div
                        initial="initial"
                        animate="animate"
                        variants={{
                            animate: { transition: { staggerChildren: 0.1 } },
                        }}
                        className="max-w-3xl text-center"
                    >
                        <motion.span
                            variants={fadeUp}
                            className="inline-block font-mono text-xs font-semibold tracking-widest uppercase text-blue-400 mb-6"
                        >
                            Legal
                        </motion.span>

                        <motion.h1
                            variants={fadeUp}
                            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
                            data-testid="privacy-hero-title"
                        >
                            Privacy Policy
                        </motion.h1>

                        <motion.p
                            variants={fadeUp}
                            className="text-lg text-slate-300 leading-relaxed"
                        >
                            Last updated: March 2026
                        </motion.p>
                    </motion.div>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
                <div className="max-w-4xl mx-auto">
                    <div className="prose prose-slate max-w-none">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="space-y-8"
                        >
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Introduction</h2>
                                <p className="text-slate-600 leading-relaxed">
                                    Vanfrank Limited ("we," "our," or "us") is committed to protecting your privacy. 
                                    This Privacy Policy explains how we collect, use, disclose, and safeguard your 
                                    information when you visit our website or use our services.
                                </p>
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Information We Collect</h2>
                                <p className="text-slate-600 leading-relaxed mb-4">
                                    We may collect information about you in a variety of ways, including:
                                </p>
                                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                                    <li><strong>Personal Data:</strong> Name, email address, phone number, company name, and other contact details you voluntarily provide through our contact forms.</li>
                                    <li><strong>Usage Data:</strong> Information about how you access and use our website, including your IP address, browser type, and pages visited.</li>
                                    <li><strong>Cookies:</strong> We use cookies and similar tracking technologies to enhance your experience on our website.</li>
                                </ul>
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-4">3. How We Use Your Information</h2>
                                <p className="text-slate-600 leading-relaxed mb-4">
                                    We use the information we collect to:
                                </p>
                                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                                    <li>Respond to your inquiries and provide customer support</li>
                                    <li>Send you information about our services that may interest you</li>
                                    <li>Improve our website and services</li>
                                    <li>Comply with legal obligations</li>
                                </ul>
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Information Sharing</h2>
                                <p className="text-slate-600 leading-relaxed">
                                    We do not sell, trade, or otherwise transfer your personal information to third parties 
                                    without your consent, except as necessary to provide our services or as required by law. 
                                    We may share information with trusted service providers who assist us in operating our 
                                    website and conducting our business.
                                </p>
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Data Security</h2>
                                <p className="text-slate-600 leading-relaxed">
                                    We implement appropriate technical and organizational measures to protect your personal 
                                    information against unauthorized access, alteration, disclosure, or destruction. However, 
                                    no method of transmission over the Internet is 100% secure, and we cannot guarantee 
                                    absolute security.
                                </p>
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Your Rights</h2>
                                <p className="text-slate-600 leading-relaxed mb-4">
                                    You have the right to:
                                </p>
                                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                                    <li>Access the personal information we hold about you</li>
                                    <li>Request correction of inaccurate information</li>
                                    <li>Request deletion of your personal information</li>
                                    <li>Opt-out of marketing communications</li>
                                </ul>
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Contact Us</h2>
                                <p className="text-slate-600 leading-relaxed">
                                    If you have any questions about this Privacy Policy, please contact us at:
                                </p>
                                <div className="mt-4 p-6 bg-slate-50 rounded-xl">
                                    <p className="text-slate-700 font-medium">Vanfrank Limited</p>
                                    <p className="text-slate-600">Vanfrank Place | 9, Olaribiro Drive, Off Allen Avenue, Ikeja</p>
                                    <p className="text-slate-600">Lagos State, Nigeria</p>
                                    <p className="text-slate-600 mt-2">Email: lagos@vanfranklimited.com</p>
                                    <p className="text-slate-600">Phone: +234 201 227 4476</p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PrivacyPage;
